import { useState, useEffect } from 'react';

import { CMS_API_URL } from '../config/apiBase';

/**
 * Fetch the no-school fallback listing for one of the brand's two fixed
 * header menu items ('menu_1' | 'menu_2'): the products + packages of the
 * menu item's default collection (falling back to the brand default
 * collection server-side). Backs the MenuItemShop pages.
 *
 * @param {string} brandSlug — brand API slug (e.g. 'clsdk')
 * @param {string} menuSlug  — the ASCII-folded menu-item label (e.g. 'vogntrojer')
 * @param {{showcase?: boolean}} [options] — `showcase: true` (brand landings): display-only
 *        teaser cards, served even when the brand's open shop is disabled.
 * When the brand's OPEN SHOP has a group linked to the menu item (admin: Brands → Open
 * shop), the cards are that group's collections and the payload also carries `group`,
 * `collections` (one section per collection: {id, displayName, subtitle, variationIds})
 * and `menuItemImages` (banner photos) — the grouped school-shop shape.
 *
 * @returns {{ menuItem: {key,label,slug}|null, collection: {id,name}|null,
 *             products: Array, packages: Array, group: object|null, collections: Array,
 *             menuItemImages: object|null, loading: boolean, error: Error|null }}
 */
export default function useMenuItemListing(brandSlug, menuSlug, { showcase = false } = {}) {
  const [data, setData] = useState({ menuItem: null, collection: null, products: [], packages: [], group: null, collections: [], menuItemImages: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brandSlug || !menuSlug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${CMS_API_URL}/api/brands/${encodeURIComponent(brandSlug)}/menu-items/${encodeURIComponent(menuSlug)}/listing${showcase ? '?showcase=1' : ''}`,
          { signal: controller.signal }
        );

        if (!res.ok) throw new Error(`Menu item listing API returned ${res.status}`);

        const json = await res.json();
        if (!cancelled) {
          setData({
            menuItem:   json.menuItem ?? null,
            collection: json.collection ?? null,
            products:   Array.isArray(json.products) ? json.products : [],
            packages:   Array.isArray(json.packages) ? json.packages : [],
            group:      json.group ?? null,
            collections: Array.isArray(json.collections) ? json.collections : [],
            menuItemImages: json.menuItemImages ?? null,
          });
        }
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          console.error(`[useMenuItemListing] Failed for "${brandSlug}/${menuSlug}":`, err);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brandSlug, menuSlug, showcase]);

  return { ...data, loading, error };
}
